<template>
  <div class="detail-page report-shell">
    <header class="report-header">
      <div class="header-grid"></div>
      <div class="header-inner">
        <div class="brand-block" @click="$router.push('/')">
          <div class="brand-mark">
            <i class="el-icon-cpu"></i>
          </div>
          <div>
            <h1 class="site-title">胡工技术分析</h1>
            <p class="site-subtitle">专注AI前沿资讯 · 洞察技术发展趋势</p>
          </div>
        </div>

        <nav class="nav-bar" aria-label="主导航">
          <button
            v-for="item in navItems"
            :key="item"
            :class="['nav-item', { active: item === (article && article.category) }]"
            type="button"
            @click="$router.push('/')"
          >
            {{ item }}
          </button>
        </nav>

        <el-input
          v-model="searchKeyword"
          placeholder="搜索AI资讯、模型、技术..."
          prefix-icon="el-icon-search"
          class="search-input"
          clearable
          @keyup.enter.native="$router.push('/')"
        />
      </div>
    </header>

    <main class="main-content">
      <div class="report-container" v-loading="loading">
        <article v-if="article" class="article-report glass-panel">
          <div class="article-meta">
            <span class="category-badge">{{ article.category || 'AI应用' }}</span>
            <span><i class="el-icon-user"></i>{{ article.author || '胡工' }}</span>
            <span><i class="el-icon-date"></i>{{ formatDate(article.createdAt) }}</span>
            <span><i class="el-icon-view"></i>{{ article.viewCount || 0 }} 次阅读</span>
          </div>

          <h1 class="article-title">{{ article.title }}</h1>
          <p class="article-summary">{{ summaryText }}</p>

          <div class="source-box">
            <span>来源</span>
            <strong>{{ sourceText }}</strong>
          </div>

          <div class="article-content" v-html="renderContent(article.content)"></div>

          <div class="article-actions">
            <button class="primary-button" type="button" @click="$router.push('/')">
              <i class="el-icon-back"></i>
              <span>返回首页</span>
            </button>
          </div>
        </article>

        <aside v-if="article" class="insight-sidebar">
          <section class="insight-card glass-panel">
            <div class="insight-heading">
              <i class="el-icon-document"></i>
              <h2>核心摘要</h2>
            </div>
            <p>{{ summaryText }}</p>
          </section>

          <section class="insight-card glass-panel">
            <div class="insight-heading">
              <i class="el-icon-collection-tag"></i>
              <h2>技术关键词</h2>
            </div>
            <div class="keyword-list">
              <span v-for="keyword in keywords" :key="keyword">{{ keyword }}</span>
            </div>
          </section>

          <section class="insight-card trend-card glass-panel">
            <div class="insight-heading">
              <i class="el-icon-data-analysis"></i>
              <h2>趋势指数</h2>
            </div>
            <div class="trend-score">8.9 /10</div>
            <p class="trend-status">强烈关注</p>
            <div class="sparkline" aria-hidden="true">
              <span></span>
            </div>
          </section>
        </aside>
      </div>
    </main>

    <footer class="site-footer">
      <div class="report-container footer-inner">
        <p>© 2026 胡工技术分析 - AI前沿资讯平台 | <a href="https://beian.miit.gov.cn/" target="_blank">粤ICP备2026030599号-2</a></p>
      </div>
    </footer>
  </div>
</template>

<script>
import api from '../api'

const fallbackArticles = [
  {
    id: 1,
    category: 'AI应用',
    title: '具身智能数据战：群核建道场，百度铺管道，京东搭舞台',
    summary: '在具身智能竞赛进入下半场的当下，数据成为新的战略高地。群核以仿真构建训练道场，百度以基础设施铺设数据管道，京东则以场景生态搭建应用舞台。',
    author: '胡工',
    createdAt: '2026-04-25',
    viewCount: 1024,
    content: '## 具身智能数据战\n具身智能的发展，正在从模型能力的比拼，转向数据体系与基础设施的全面较量。谁能构建更高质量的训练场景、更高效的数据通路与更丰富的落地生态，谁就能在下一轮产业变革中占据先机。'
  },
  {
    id: 2,
    category: 'AI资讯',
    title: 'AI幻觉越来越多，「智障率」已经让人无法容忍了',
    summary: '模型能力快速增长的同时，幻觉、错答和不稳定输出正在成为产品化落地中必须正视的问题。',
    author: '胡工',
    createdAt: '2026-04-25',
    viewCount: 856,
    content: '## 幻觉问题进入工程阶段\nAI 幻觉不再只是论文指标里的缺陷，而是产品可信度、工作流稳定性和企业采用成本中的核心变量。'
  }
]

export default {
  name: 'ArticleDetail',
  data() {
    return {
      article: null,
      loading: false,
      searchKeyword: '',
      navItems: ['首页', 'AI资讯', '深度分析', '大模型', 'AI应用', 'RAG技术', '国产AI', 'AI Agent']
    }
  },
  computed: {
    summaryText() {
      return (this.article && this.article.summary) || '这篇文章聚焦 AI 技术与产业变化，提炼关键趋势、落地路径和需要持续观察的信号。'
    },
    sourceText() {
      const content = (this.article && this.article.content) || ''
      const match = content.match(/来源[：:]\s*([^\n，,。*]+)/)
      return match ? match[1].trim() : '胡工技术分析'
    },
    keywords() {
      const base = ['AI应用', '大模型', '技术趋势', '产业观察']
      const title = (this.article && this.article.title) || ''
      const category = this.article && this.article.category
      const candidates = [
        category,
        ...base,
        ...['具身智能', '数据战', 'RAG技术', 'AI Agent', '国产AI'].filter(word => title.includes(word))
      ].filter(Boolean)
      return Array.from(new Set(candidates)).slice(0, 8)
    }
  },
  created() {
    this.loadArticle()
  },
  methods: {
    async loadArticle() {
      this.loading = true
      try {
        const { data } = await api.getArticle(this.$route.params.id)
        this.article = data
      } catch {
        const id = Number(this.$route.params.id)
        this.article = fallbackArticles.find(article => article.id === id) || fallbackArticles[0]
      } finally {
        this.loading = false
      }
    },
    renderContent(content) {
      if (!content) return ''
      return content
        .replace(/---\s*\n.*本文由胡工技术分析.*$/s, '')
        .replace(/## (.+)/g, '<h2>$1</h2>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br>')
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.report-shell {
  min-height: 100vh;
  background:
    radial-gradient(circle at 16% 4%, rgba(47, 140, 255, 0.24), transparent 30%),
    radial-gradient(circle at 86% 18%, rgba(79, 220, 255, 0.12), transparent 24%),
    linear-gradient(180deg, #030814 0%, #07111f 44%, #030814 100%);
  color: #f4f8ff;
}

.report-header {
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid rgba(116, 170, 255, 0.18);
}

.header-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(116, 170, 255, 0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(116, 170, 255, 0.07) 1px, transparent 1px);
  background-size: 38px 38px;
  opacity: 0.7;
}

.header-inner,
.report-container {
  position: relative;
  max-width: 1540px;
  margin: 0 auto;
  padding: 0 32px;
}

.header-inner {
  min-height: 116px;
  display: grid;
  grid-template-columns: minmax(310px, 1fr) auto minmax(320px, 420px);
  gap: 22px;
  align-items: center;
}

.brand-block {
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
}

.brand-mark {
  width: 54px;
  height: 54px;
  display: grid;
  place-items: center;
  border-radius: 16px;
  color: #4fdcff;
  font-size: 30px;
  background: rgba(47, 140, 255, 0.16);
  border: 1px solid rgba(116, 170, 255, 0.34);
}

.site-title {
  margin: 0 0 4px;
  font-size: 25px;
  font-weight: 800;
  line-height: 1.2;
}

.site-subtitle {
  margin: 0;
  color: #aebbd0;
  font-size: 13px;
}

.nav-bar {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
}

.nav-bar::-webkit-scrollbar {
  display: none;
}

.nav-item {
  flex: 0 0 auto;
  height: 38px;
  padding: 0 13px;
  color: #aebbd0;
  border: 1px solid rgba(116, 170, 255, 0.18);
  border-radius: 999px;
  background: rgba(9, 20, 36, 0.56);
  cursor: pointer;
}

.nav-item.active,
.nav-item:hover {
  color: #ffffff;
  border-color: rgba(79, 220, 255, 0.58);
  background: rgba(47, 140, 255, 0.22);
}

.search-input >>> .el-input__inner {
  height: 46px;
  color: #f4f8ff;
  background: rgba(9, 20, 36, 0.72);
  border: 1px solid rgba(116, 170, 255, 0.28);
  border-radius: 15px;
}

.search-input >>> .el-input__inner::placeholder {
  color: #7f8ca3;
}

.main-content {
  padding: 34px 0 32px;
}

.report-container {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 390px;
  gap: 24px;
}

.glass-panel {
  background: rgba(9, 20, 36, 0.72);
  border: 1px solid rgba(116, 170, 255, 0.22);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.36), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(18px);
}

.article-report,
.insight-card {
  border-radius: 20px;
}

.article-report {
  padding: 36px;
}

.article-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  color: #aebbd0;
  font-size: 14px;
}

.article-meta span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.category-badge {
  min-height: 32px;
  padding: 0 12px;
  border-radius: 10px;
  color: #03101d;
  font-weight: 800;
  background: linear-gradient(135deg, #4fdcff, #2f8cff);
}

.article-title {
  margin: 26px 0 18px;
  color: #ffffff;
  font-size: 42px;
  line-height: 1.28;
  font-weight: 800;
}

.article-summary {
  margin: 0;
  padding: 20px 22px;
  color: #dbe8ff;
  font-size: 17px;
  line-height: 1.85;
  border-left: 4px solid #4fdcff;
  border-radius: 16px;
  background: rgba(47, 140, 255, 0.1);
}

.source-box {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 28px 0;
  padding: 16px 18px;
  border-radius: 14px;
  background: rgba(3, 8, 20, 0.5);
  border: 1px solid rgba(116, 170, 255, 0.18);
}

.source-box span {
  color: #7f8ca3;
}

.source-box strong {
  color: #f5b94f;
}

.article-content {
  color: #dbe8ff;
  font-size: 16px;
  line-height: 1.95;
}

.article-content >>> h2 {
  margin: 32px 0 16px;
  color: #ffffff;
  font-size: 24px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(79, 220, 255, 0.34);
}

.article-content >>> strong {
  color: #4fdcff;
}

.article-actions {
  margin-top: 34px;
  padding-top: 26px;
  border-top: 1px solid rgba(116, 170, 255, 0.16);
}

.primary-button {
  height: 52px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 22px;
  color: #ffffff;
  border: 0;
  border-radius: 15px;
  background: linear-gradient(135deg, #2f8cff, #4fdcff);
  cursor: pointer;
  box-shadow: 0 16px 36px rgba(47, 140, 255, 0.24);
}

.insight-sidebar {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.insight-card {
  padding: 22px;
}

.insight-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.insight-heading i {
  color: #4fdcff;
  font-size: 20px;
}

.insight-heading h2 {
  margin: 0;
  font-size: 18px;
}

.insight-card p {
  margin: 0;
  color: #aebbd0;
  font-size: 14px;
  line-height: 1.8;
}

.keyword-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.keyword-list span {
  padding: 8px 10px;
  color: #dbe8ff;
  border: 1px solid rgba(116, 170, 255, 0.22);
  border-radius: 10px;
  background: rgba(47, 140, 255, 0.1);
  font-size: 13px;
}

.trend-score {
  color: #f5b94f;
  font-size: 42px;
  font-weight: 800;
}

.trend-status {
  color: #f5b94f !important;
  font-weight: 700;
}

.sparkline {
  height: 64px;
  margin-top: 16px;
  border-radius: 12px;
  background:
    linear-gradient(135deg, transparent 42%, rgba(245, 185, 79, 0.8) 43% 45%, transparent 46%),
    linear-gradient(20deg, transparent 35%, rgba(79, 220, 255, 0.52) 36% 38%, transparent 39%),
    rgba(3, 8, 20, 0.48);
  border: 1px solid rgba(245, 185, 79, 0.22);
}

.site-footer {
  padding: 10px 0 34px;
  color: #7f8ca3;
  font-size: 13px;
}

.footer-inner {
  display: block;
  text-align: center;
}

.site-footer a {
  color: #4fdcff;
}

@media screen and (max-width: 1180px) {
  .header-inner {
    grid-template-columns: 1fr;
    padding-top: 22px;
    padding-bottom: 22px;
  }

  .report-container {
    grid-template-columns: 1fr;
  }

  .insight-sidebar {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media screen and (max-width: 768px) {
  .header-inner,
  .report-container {
    padding-left: 16px;
    padding-right: 16px;
  }

  .brand-mark {
    width: 48px;
    height: 48px;
    font-size: 26px;
  }

  .site-title {
    font-size: 21px;
  }

  .article-report,
  .insight-card {
    border-radius: 16px;
  }

  .article-report {
    padding: 22px;
  }

  .article-title {
    font-size: 28px;
  }

  .article-summary,
  .article-content {
    font-size: 15px;
  }

  .insight-sidebar {
    grid-template-columns: 1fr;
  }
}
</style>
